import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Flex,
  Icon,
  IconButton,
  Input,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import { useSelect } from 'downshift';
import { FiChevronDown, FiChevronUp, FiX } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

type SelectOptionColor = {
  bg: string;
  text: string;
};

export type MultipleOptionsSelectOption = {
  id: string | number;
  name: string;
  additionalInfo?: string;
  color?: SelectOptionColor;
};

type SelectValue = string | number;

type Props = {
  options?: MultipleOptionsSelectOption[];
  placeholder?: string;
  size?: string;
  variant?: string;
  side?: 'left' | 'right';
  selectedIds?: SelectValue[];
  isDisabled?: boolean;
  isCleareable?: boolean;
  isFilterable?: boolean;
  showUnfilteredResults?: boolean;
  onChange: (selectedIds: SelectValue[]) => void;
};

const DEFAULT_MIN_STRING_LENGTH = 3;

const MultipleOptionsSelectFieldWithOutsideTags = ({
  options = [],
  placeholder: placeholderProp,
  size,
  variant,
  side = 'right',
  selectedIds = [],
  isDisabled,
  isCleareable,
  isFilterable,
  showUnfilteredResults,
  onChange,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const styles = useMultiStyleConfig('SelectMultipleWithOutsideTags', {
    size,
    variant,
    side,
  });
  const [query, setQuery] = useState('');
  const [selectedIdsState, setSelectedIdsState] = useState<SelectValue[]>(
    selectedIds,
  );
  const placeholder = placeholderProp ?? t('chooseOptions', 'Wybierz opcje');

  useEffect(() => {
    setSelectedIdsState(selectedIds);
  }, [selectedIds]);

  const selectedItemsFromProps = useMemo(
    () =>
      selectedIdsState
        .map(selectedId => options.find(option => option.id === selectedId))
        .filter(
          (item): item is MultipleOptionsSelectOption => item !== undefined,
        ),
    [options, selectedIdsState],
  );

  const sortSelectedIdsByOptions = (ids: SelectValue[]) =>
    ids.slice().sort((leftId, rightId) => {
      const leftIndex = options.findIndex(option => option.id === leftId);
      const rightIndex = options.findIndex(option => option.id === rightId);
      return (
        (leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex) -
        (rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex)
      );
    });

  const isItemSelected = (checkedItem: MultipleOptionsSelectOption) =>
    selectedItemsFromProps.some(
      (selectedItem: MultipleOptionsSelectOption) =>
        selectedItem?.id === checkedItem?.id,
    );

  const toggleItem = (item: MultipleOptionsSelectOption) => {
    const nextSelectedIds = isItemSelected(item)
      ? selectedIdsState.filter(selectedId => selectedId !== item.id)
      : [...selectedIdsState, item.id];

    const sortedNextSelectedIds = sortSelectedIdsByOptions(nextSelectedIds);

    setSelectedIdsState(sortedNextSelectedIds);
    onChange(sortedNextSelectedIds);
  };

  const handleClear = () => {
    setSelectedIdsState([]);
    onChange([]);
  };

  const handleRemoveItem = (item: MultipleOptionsSelectOption) => {
    const nextSelectedIds = selectedIdsState.filter(
      selectedId => selectedId !== item.id,
    );

    const sortedNextSelectedIds = sortSelectedIdsByOptions(nextSelectedIds);

    setSelectedIdsState(sortedNextSelectedIds);
    onChange(sortedNextSelectedIds);
  };

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
  } = useSelect<MultipleOptionsSelectOption>({
    selectedItem: null,
    defaultHighlightedIndex: 0,
    items: options,
    itemToString: item => item?.name || '',
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useSelect.stateChangeTypes.MenuKeyDownEnter:
        case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true,
          };
        default:
          return changes;
      }
    },
    onStateChange: ({ type, selectedItem }) => {
      switch (type) {
        case useSelect.stateChangeTypes.MenuKeyDownEnter:
        case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          if (selectedItem) {
            toggleItem(selectedItem);
          }
          break;
        default:
          break;
      }
    },
  });

  const filteredOptions = useMemo(() => {
    if (!isFilterable || showUnfilteredResults) {
      return options;
    }

    if (query.length < DEFAULT_MIN_STRING_LENGTH) {
      return [];
    }

    return options.filter(option =>
      option.name?.toLowerCase().includes(query.toLowerCase()),
    );
  }, [isFilterable, options, query, showUnfilteredResults]);

  const shouldShowNoResults =
    isFilterable &&
    !showUnfilteredResults &&
    query.length >= DEFAULT_MIN_STRING_LENGTH &&
    filteredOptions.length === 0;

  const menuProps = getMenuProps({}, { suppressRefError: true });
  const { ref: menuRef, ...menuContainerProps } = menuProps;

  return (
    <Flex __css={styles.wrapper}>
      <Box __css={styles.fieldWrapper}>
        <Flex
          as='button'
          type='button'
          sx={styles.field}
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          opacity={isDisabled ? 0.6 : 1}
          {...getToggleButtonProps({ 'aria-label': placeholder })}
        >
          <Box sx={styles.fieldLabel}>{placeholder}</Box>
          {isCleareable && (
            <IconButton
              aria-label={t('clearSelectedOption', 'Clear selected option')}
              icon={<FiX />}
              size='xs'
              variant='ghost'
              isDisabled={isDisabled || selectedIdsState.length === 0}
              onClick={e => {
                e.stopPropagation();
                handleClear();
              }}
              __css={styles.clearButton}
            />
          )}
          <Icon as={isOpen ? FiChevronUp : FiChevronDown} __css={styles.icon} />
        </Flex>

        {isOpen && (
          <Box
            as='ul'
            role='listbox'
            ref={menuRef}
            {...menuContainerProps}
            __css={styles.dropdown}
          >
            {isFilterable && (
              <Box mb={2}>
                <Input
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={t(
                    'enterMinStringLength',
                    'Wpisz przynajmniej {{minStringLengthRequired}} znaki',
                    { minStringLengthRequired: DEFAULT_MIN_STRING_LENGTH },
                  )}
                  size='sm'
                />
              </Box>
            )}

            <Box __css={styles.options}>
              {shouldShowNoResults ? (
                <Alert status='info'>
                  {t('noResultsFound', 'Nie znaleziono wyników')}
                </Alert>
              ) : (
                filteredOptions.map((item, index) => {
                  const itemProps = getItemProps({ item, index });
                  const isSelected = isItemSelected(item);

                  return (
                    <Flex
                      key={item.id}
                      as='li'
                      sx={styles.option}
                      {...itemProps}
                    >
                      <Checkbox
                        isChecked={isSelected}
                        tabIndex={-1}
                        sx={styles.checkbox}
                      />

                      <Box minW={0}>
                        <Text sx={styles.optionText}>{item.name}</Text>
                        {item.additionalInfo ? (
                          <Text sx={styles.optionInfo}>
                            {item.additionalInfo}
                          </Text>
                        ) : null}
                      </Box>
                    </Flex>
                  );
                })
              )}
            </Box>
          </Box>
        )}
      </Box>

      {selectedItemsFromProps.length > 0 && (
        <Flex sx={styles.tags}>
          {selectedItemsFromProps.map((item: MultipleOptionsSelectOption) => (
            <Tag
              key={item.id}
              size='md'
              bg={item.color?.bg ?? 'gray.200'}
              color={item.color?.text ?? 'graphite.500'}
              sx={styles.tag}
              onClick={() => !isDisabled && handleRemoveItem(item)}
            >
              <TagLabel isTruncated={false} lineHeight='normal'>{item.name}</TagLabel>
              <TagCloseButton
                onClick={e => {
                  e.stopPropagation();
                  if (!isDisabled) {
                    handleRemoveItem(item);
                  }
                }}
              />
            </Tag>
          ))}
        </Flex>
      )}
    </Flex>
  );
};

export default MultipleOptionsSelectFieldWithOutsideTags;
